// Pure, dependency-free helpers for the Backtest Robustness views (permutation test + execution
// stress). No React / store / DOM imports, so they unit-test in isolation. The React components
// re-export nothing from here except these functions.

import { TERMINAL_JOB_STATUSES } from "@src/utils/jobChildren";

export type Verdict = "good" | "warn" | "bad";

export { TERMINAL_JOB_STATUSES };

/** The subset of a monte_carlo job this module needs to decide what to show. */
export interface PartialAwareRun {
  status_name: string;
  result?: { data?: { monte_carlo?: { partial?: boolean; stop_reason?: string } } };
}

/**
 * Whether a run has an aggregate worth showing: it completed normally, or it published an
 * incomplete aggregate — salvaged after stopping early, or covering fewer children than planned.
 *
 * Deliberately NOT "has a result payload". A plain failure has one too — just
 * `{schema_version, status, message}` — and the aggregate view treats any non-permutation payload
 * as execution stress, so admitting plain failures would render a failed permutation run as
 * "No execution-stress scenarios". A still-running checkpoint also carries `partial: true` but is
 * not a result yet, which is what the terminal check excludes.
 */
export const hasViewableResult = (run: PartialAwareRun): boolean =>
  run.status_name === "completed" || isPartialRun(run);

/**
 * An incomplete run — its aggregate covers fewer children than the run planned, either because it
 * stopped before finishing or because children went missing from the distribution while it ran.
 *
 * Terminal-only, like `hasViewableResult`. A healthy run in flight also carries `partial: true`,
 * because its checkpoints announce themselves as not-yet-a-result; badging those would mark every
 * normal run partial for its whole duration.
 */
export const isPartialRun = (run: PartialAwareRun): boolean =>
  TERMINAL_JOB_STATUSES.has(run.status_name) && run.result?.data?.monte_carlo?.partial === true;

type MetricFormat = "percent" | "ratio" | "fraction" | "count" | "money" | "raw";

// How ColoredValue (monospaced, tabular) should render a metric: money as currency, *_pct and the
// 0..1 win_rate fraction as a percent (fraction scaled ×100), everything else a plain number.
export type ColoredKind = "currency" | "percentage" | "number";

export const metricColored = (
  value: number | null | undefined,
  metric: string,
): { value: number | null; kind: ColoredKind; decimals: number } => {
  const v = value == null || Number.isNaN(value) ? null : value;
  const format = METRIC_META[metric]?.format ?? "raw";
  switch (format) {
    case "money":
      return { value: v, kind: "currency", decimals: 2 };
    case "percent":
      return { value: v, kind: "percentage", decimals: 2 };
    case "fraction":
      return { value: v == null ? null : v * 100, kind: "percentage", decimals: 1 };
    case "ratio":
      return { value: v, kind: "number", decimals: 2 };
    case "count":
      return { value: v, kind: "number", decimals: 0 };
    default:
      return { value: v, kind: "number", decimals: 3 };
  }
};

// Label + display format per BacktestMetrics field the coordinator can report. Unknown metrics fall
// back to a humanized key + "raw" number, so a new backend metric still renders (just less pretty).
const METRIC_META: Record<string, { label: string; format: MetricFormat }> = {
  cumulative_returns_pct: { label: "Cumulative return", format: "percent" },
  annualized_return_pct: { label: "Annualized return", format: "percent" },
  cash_on_cash_return_pct: { label: "Cash-on-cash return", format: "percent" },
  avg_win_pct: { label: "Avg win", format: "percent" },
  avg_loss_pct: { label: "Avg loss", format: "percent" },
  max_drawdown_pct: { label: "Max drawdown", format: "percent" },
  sharpe_ratio: { label: "Sharpe", format: "ratio" },
  sortino_ratio: { label: "Sortino", format: "ratio" },
  calmar_ratio: { label: "Calmar", format: "ratio" },
  profit_factor: { label: "Profit factor", format: "ratio" },
  win_loss_ratio: { label: "Win/loss ratio", format: "ratio" },
  win_rate: { label: "Win rate", format: "fraction" },
  total_positions: { label: "Positions", format: "count" },
  win_signals: { label: "Wins", format: "count" },
  loss_signals: { label: "Losses", format: "count" },
  cumulative_returns: { label: "Cumulative return", format: "money" },
  ending_capital: { label: "Ending capital", format: "money" },
  invested_capital: { label: "Invested capital", format: "money" },
  avg_win_amount: { label: "Avg win", format: "money" },
  avg_loss_amount: { label: "Avg loss", format: "money" },
  max_drawdown: { label: "Max drawdown", format: "money" },
  skewness: { label: "Skewness", format: "raw" },
  kurtosis: { label: "Kurtosis", format: "raw" },
};

// Mirror of the backend metric-direction map (crypto_lib/analysis/metric_directions.py). Drawdowns
// are stored negative but compared on magnitude, so their user-facing direction is "lower is better".
const LOWER_IS_BETTER = new Set(["loss_signals", "kurtosis", "max_drawdown_pct", "max_drawdown"]);

export const metricLabel = (metric: string): string =>
  METRIC_META[metric]?.label ?? metric.replace(/_/g, " ");

export const metricDirection = (metric: string): "higher" | "lower" =>
  LOWER_IS_BETTER.has(metric) ? "lower" : "higher";

export const formatMetric = (value: number | null | undefined, metric: string): string => {
  if (value == null || Number.isNaN(value)) return "—";
  const format = METRIC_META[metric]?.format ?? "raw";
  switch (format) {
    case "percent":
      return `${value.toFixed(2)}%`;
    case "fraction":
      return `${(value * 100).toFixed(1)}%`;
    case "ratio":
      return value.toFixed(2);
    case "count":
      return value.toFixed(0);
    case "money":
      return value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`;
    case "raw":
      return value.toFixed(3);
  }
};

// Signed delta with an explicit +/- and the metric's own format, for the scenario table. A positive
// delta already means "better than baseline" (the backend orients it on the comparison value).
export const formatDelta = (delta: number | null | undefined, metric: string): string => {
  if (delta == null || Number.isNaN(delta)) return "—";
  const sign = delta > 0 ? "+" : delta < 0 ? "" : "";
  return `${sign}${formatMetric(delta, metric)}`;
};

export type ScenarioVerdict = "robust" | "weakened" | "fragile";

export interface ScenarioMetricValue {
  value: number | null;
}

// One economic verdict for an execution-stress scenario (per the second-opinion: rate the scenario
// once against pre-registered gates, never per-cell % bands). Profitability floors come from the
// scenario's own metrics; the risk floor from the operator's pre-registered drawdown limit.
// Baseline-relative weakening only downgrades robust -> weakened; it never makes a scenario fragile.
// Returns null when neither profitability gate metric is present (custom result_metrics), so an
// unevaluable scenario is never called "robust".
export const rateScenario = (
  metrics: Record<string, ScenarioMetricValue | undefined>,
  baseline: Record<string, number | null>,
  maxDrawdownPct: number | null | undefined,
): ScenarioVerdict | null => {
  const pf = metrics.profit_factor?.value;
  const ret = metrics.cumulative_returns_pct?.value;
  const dd = metrics.max_drawdown_pct?.value; // stored negative

  // Fragile: the scenario breaks an economic floor (any present metric can trip this).
  if (pf != null && pf < 1) return "fragile";
  if (ret != null && ret <= 0) return "fragile";
  if (maxDrawdownPct != null && dd != null && Math.abs(dd) > maxDrawdownPct) return "fragile";

  // Without either profitability metric there's no basis to confirm the edge survived.
  if (pf == null && ret == null) return null;

  // Weakened: still profitable, but profit factor retained under two-thirds of the baseline's.
  const basePf = baseline.profit_factor;
  if (pf != null && basePf != null && basePf > 0 && pf / basePf < 2 / 3) return "weakened";

  return "robust";
};

export const SCENARIO_VERDICT_META: Record<ScenarioVerdict, { verdict: Verdict; label: string }> = {
  robust: { verdict: "good", label: "Robust" },
  weakened: { verdict: "warn", label: "Weakened" },
  fragile: { verdict: "bad", label: "Fragile" },
};

export const scenarioVerdictHelp = (maxDrawdownPct: number | null | undefined): string => {
  const dd = maxDrawdownPct != null ? ` or drawdown past your ${maxDrawdownPct}% limit` : "";
  return (
    "Robust: still profitable and profit factor kept at least two-thirds of baseline. " +
    "Weakened: profitable but profit factor fell more than a third. " +
    `Fragile: profit factor under 1, return not positive${dd}.`
  );
};

// p-value verdict for "is the edge real, or curve-fit noise?". Significant (<=0.05) is the good
// outcome: the real result beats the synthetic noise distribution. Marginal is a warning, and a high
// p-value means the strategy did not separate from noise.
export const ratePValue = (pValue: number | null | undefined): Verdict | null => {
  if (pValue == null || Number.isNaN(pValue)) return null;
  if (pValue <= 0.05) return "good";
  if (pValue <= 0.1) return "warn";
  return "bad";
};

export const formatPValue = (pValue: number | null | undefined): string =>
  pValue == null || Number.isNaN(pValue) ? "—" : pValue.toFixed(4);

/** The coverage one permutation distribution has, and the run-wide marker sitting over it. */
export interface MetricCoverage {
  /** The payload's own `partial` marker — a statement about the run, not about this distribution. */
  runIsPartial: boolean;
  /** Replicates that produced a usable value for THIS distribution. */
  validReplicates: number;
  /** Replicates the run planned. */
  plannedReplicates: number;
}

/**
 * Whether one permutation p-value is rendered without a significance verdict.
 *
 * Two independent reasons, and either is enough. The first is `runIsPartial`, the payload's own
 * statement that it covers less than the planned run. It is a claim about the RUN — the coordinator
 * writes it from several situations, one of which is not a replicate count at all — so it says
 * nothing about which metrics are short.
 *
 * The second is this distribution's own coverage. A `per_metric` row carries its own
 * `valid_replicates`, because a replicate whose value for THIS metric is null or NaN is dropped
 * before that row's p-value is computed. Profit factor, for one, is null for a child whose return
 * series has no negative bar. So a permutation run whose ranking distribution is whole — no
 * run-wide marker, verdicts shown — can still hold a short distribution for another metric, and
 * colouring that row would rate a censored set as a finding.
 *
 * Takes an object, not positional numbers: both counts are plain `number`, so a swapped pair or a
 * run-wide count substituted for a row's own would compile and silently restore the behaviour this
 * withholding exists to remove.
 */
export const metricVerdictWithheld = ({
  runIsPartial,
  validReplicates,
  plannedReplicates,
}: MetricCoverage): boolean => runIsPartial || validReplicates < plannedReplicates;

/**
 * Why one p-value's verdict is withheld, as a phrase to show beside the number — `""` when it is
 * not withheld.
 *
 * A number rendered without its traffic light and without this reads as "the page had nothing to
 * say about it", which is the opposite of what happened. The run-wide case names itself, because
 * that marker is not a replicate count and this row's counts may well be whole; otherwise the
 * phrase names the coverage the p-value was actually computed over.
 */
export const metricWithheldReason = (coverage: MetricCoverage): string => {
  if (!metricVerdictWithheld(coverage)) return "";
  return coverage.runIsPartial
    ? "partial run"
    : `${coverage.validReplicates} of ${coverage.plannedReplicates} replicates`;
};

// Replicate-count power label, as returned by the backend (power_label). Rigorous is the reportable
// bar; insufficient means the p-value is meaningless.
export const POWER_VERDICT: Record<string, Verdict> = {
  insufficient: "bad",
  low: "warn",
  exploratory: "warn",
  rigorous: "good",
};

export const powerHelp = (power: string): string => {
  switch (power) {
    case "insufficient":
      return "Under 30 valid replicates. The p-value is not meaningful — raise the replicate count.";
    case "low":
      return "30 to 99 replicates. Enough for a rough read, not for a reportable result.";
    case "exploratory":
      return "100 to 999 replicates. Fine for screening; 200 gives resolution near 0.005.";
    case "rigorous":
      return "1000+ replicates. The recommended bar for a result you would report.";
    default:
      return "";
  }
};

export const VERDICT_CLASS: Record<Verdict, string> = {
  good: "text-success",
  warn: "text-warning",
  bad: "text-danger",
};

// The five documented execution-stress presets, expanded client-side into scenario configs. Kept as
// data so the launch form renders them as checkboxes and the payload builder stays declarative.
export interface StressScenarioInput {
  name: string;
  fee_multiplier?: number;
  slippage_multiplier?: number;
  funding_multiplier?: number;
  entry_skip_probability?: number;
  entry_delay_candles?: number;
  replicates?: number;
}

export const STRESS_PRESETS: { key: string; label: string; scenario: StressScenarioInput }[] = [
  { key: "fees_1_5x", label: "Fees 1.5x", scenario: { name: "fees_1_5x", fee_multiplier: 1.5 } },
  {
    key: "slippage_2x",
    label: "Slippage 2x",
    scenario: { name: "slippage_2x", slippage_multiplier: 2.0 },
  },
  {
    key: "fees_1_5x_slip_2x",
    label: "Fees 1.5x + slippage 2x",
    scenario: { name: "fees_1_5x_slip_2x", fee_multiplier: 1.5, slippage_multiplier: 2.0 },
  },
  {
    key: "skip_10pct",
    label: "Skip 10% of entries",
    scenario: { name: "skip_10pct", entry_skip_probability: 0.1, replicates: 20 },
  },
  {
    key: "delay_1_candle",
    label: "Delay entries 1 candle",
    scenario: { name: "delay_1_candle", entry_delay_candles: 1 },
  },
];
